import {useRef, useState} from 'react';

import {equals, isNil, omit, pick, reject} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {MinipurchaseResponseBody} from '@dms/api/finance';

import {Nullish} from 'shared/web';

import type {ExternalFilters, GridDataChange, QueryFilterObject} from 'features/eag-grid';

import {makeDataGridFilter} from '../utils/makeDataGridFilter';
import {transformMinipurchaseToExternalFilter} from '../utils/transformMinipurchaseToExternalFilter';

type UseSpecificationGridSyncParams = {
  interestId: string;
  miniPurchase: MinipurchaseResponseBody | Nullish;
  isMinipurchaseFetching: boolean;
  queryModifier: (filter: QueryFilterObject) => QueryFilterObject;
};

/**
 * Syncs the saved request specification into the offerable vehicles grid.
 *
 * EagGrid ignores a filter pushed before its first fetch settles (it reuses the preset query),
 * so the specification goes to the grid only after that fetch. Each interest and specification
 * syncs once, and only when the grid does not show it yet (a stored or URL query can already
 * hold it). The specification owns its own keys; the grid's other filters stay.
 */
export function useSpecificationGridSync(params: UseSpecificationGridSyncParams) {
  // The interest whose grid has settled its first fetch. Compared with interestId, so it resets per interest.
  const [gridLoadedInterestId, setGridLoadedInterestId] = useState<string | null>(null);
  const syncedSpecificationKeyRef = useRef<string | null>(null);

  const specificationFilter = makeDataGridFilter(
    transformMinipurchaseToExternalFilter(params.miniPurchase)
  );
  const isSyncReady = gridLoadedInterestId === params.interestId && !params.isMinipurchaseFetching;
  const specificationSyncKey = isSyncReady
    ? `${params.interestId}:${JSON.stringify(specificationFilter)}`
    : null;

  const handleGridDataChange = (data: GridDataChange) => {
    if (data.fetchState === 'success') {
      setGridLoadedInterestId(params.interestId);
    }
  };

  const syncSpecification = (
    gridValues: ExternalFilters,
    onGridFilterChange: (filter: ExternalFilters) => void
  ) => {
    if (isNil(specificationSyncKey) || syncedSpecificationKeyRef.current === specificationSyncKey) {
      return;
    }
    syncedSpecificationKeyRef.current = specificationSyncKey;

    // The grid drops empty filter values, so the specification drops them too before the compare.
    const specificationKeys = Object.keys(specificationFilter);
    const isSpecificationInGrid = equals(
      reject(isNilOrEmpty, specificationFilter),
      pick(specificationKeys, gridValues)
    );

    if (!isSpecificationInGrid) {
      // queryModifier keys are left out: the grid adds them to every query itself.
      const staticFilterKeys = Object.keys(params.queryModifier({}));
      onGridFilterChange({
        ...omit([...specificationKeys, ...staticFilterKeys], gridValues),
        ...specificationFilter,
      });
    }
  };

  return {specificationSyncKey, handleGridDataChange, syncSpecification};
}
